import {X} from 'lucide-react';
import * as React from 'react';
import {cn} from '../lib/cn';

type VideoModalProps = {
  videoId: string;
  isOpen: boolean;
  onClose: () => void;
  width?: number | string;
  height?: number | string;
};

function VideoModal({
  videoId,
  isOpen,
  onClose,
  width = '100%',
  height = '100%',
}: VideoModalProps) {
  const iframeRef = React.useRef<HTMLIFrameElement>(null);

  React.useEffect(() => {
    const handleEsc = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        onClose();
      }
    };

    if (isOpen) {
      window.addEventListener('keydown', handleEsc);
    }

    return () => {
      window.removeEventListener('keydown', handleEsc);
    };
  }, [isOpen, onClose]);

  React.useEffect(() => {
    if (!isOpen && iframeRef.current) {
      iframeRef.current.contentWindow?.postMessage(
        '{"event":"command","func":"pauseVideo","args":""}',
        '*',
      );
    }
  }, [isOpen]);

  if (!isOpen) {
    return null;
  }

  return (
    <div
      className="fixed inset-0 z-[9999] flex items-center justify-center bg-[var(--video-modal-overlay-bg)] px-4"
      role="dialog"
      aria-modal="true"
      aria-label="Video"
    >
      <div className="relative w-full max-w-[1075px]">
        <button
          type="button"
          onClick={onClose}
          className={cn(
            'absolute -right-10 top-0 z-[9999] flex h-8 w-8 items-center justify-center rounded-full',
            'bg-[var(--video-modal-close-bg)] text-[var(--video-modal-close-color)] shadow-sm transition-shadow hover:shadow-md',
            'max-sm:right-2 max-sm:top-2',
          )}
          aria-label="Close"
        >
          <X className="h-5 w-5" aria-hidden />
        </button>
        <div className="relative w-full overflow-hidden">
          <iframe
            ref={iframeRef}
            src={`https://www.youtube.com/embed/${videoId}?autoplay=0&rel=0&showinfo=0`}
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
            allowFullScreen
            width={width}
            height={height}
            title="Video"
            className="border-0"
          />
        </div>
      </div>
    </div>
  );
}

export {VideoModal};
export type {VideoModalProps};
